import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion, useScroll, useSpring } from "framer-motion";
import { isLowPower, isSlowConnection, routeKey } from "../utils/perf";

// Per-page 4K environments. Live ambient video ONLY on the front page and
// Visit & Contact (owner's call) — it stays dim and blended, never the focal
// point. Every other page gets a static 4K tech/space/city picture.
const BG_BY_ROUTE = {
  "/": { poster: "/env/city4k.jpg", video: "/videos/city-bg.mp4" },
  "/repair-lab": { poster: "/env/station4k.jpg", video: null },
  "/quote": { poster: "/env/cyber4k.jpg", video: null },
  "/phones": { poster: "/env/galaxy4k.jpg", video: null },
  "/showroom": { poster: "/env/tech4k.jpg", video: null },
  "/visit": { poster: "/env/city4k.jpg", video: "/videos/visit-bg.mp4" },
};
const DEFAULT_BG = BG_BY_ROUTE["/"];

const BLOBS = [
  { x: "-12%", y: "-10%", size: 540, color: "rgba(233,180,76,0.20)", dur: 26 },
  { x: "82%", y: "-14%", size: 480, color: "rgba(46,107,230,0.22)", dur: 32 },
  { x: "-14%", y: "44%", size: 440, color: "rgba(79,179,191,0.16)", dur: 28, mobileHide: true },
  { x: "88%", y: "52%", size: 500, color: "rgba(61,111,224,0.18)", dur: 36 },
  { x: "-10%", y: "86%", size: 460, color: "rgba(124,131,219,0.16)", dur: 30, mobileHide: true },
  { x: "76%", y: "88%", size: 420, color: "rgba(139,92,255,0.18)", dur: 34 },
];

const BOKEH = [
  { x: "6%", y: "22%", s: 10, c: "rgba(231,206,154,0.8)", d: 14 },
  { x: "12%", y: "68%", s: 7, c: "rgba(255,181,154,0.7)", d: 18 },
  { x: "30%", y: "84%", s: 8, c: "rgba(231,206,154,0.6)", d: 16 },
  { x: "70%", y: "18%", s: 6, c: "rgba(255,181,154,0.7)", d: 15 },
  { x: "78%", y: "72%", s: 11, c: "rgba(79,179,191,0.55)", d: 19 },
  { x: "88%", y: "38%", s: 7, c: "rgba(231,206,154,0.75)", d: 13 },
  { x: "48%", y: "8%", s: 6, c: "rgba(61,111,224,0.55)", d: 20 },
  { x: "55%", y: "92%", s: 8, c: "rgba(124,131,219,0.5)", d: 16 },
];

const SCREWS = [
  { x: "9%", y: "30%", s: 15, d: 24 },
  { x: "16%", y: "78%", s: 12, d: 30 },
  { x: "42%", y: "12%", s: 13, d: 27 },
  { x: "64%", y: "86%", s: 16, d: 22 },
  { x: "83%", y: "26%", s: 12, d: 32 },
  { x: "91%", y: "62%", s: 14, d: 26 },
];

// Twinkling starfield — deterministic pseudo-random spread (stable across renders).
const STARS = Array.from({ length: 42 }, (_, i) => {
  const r = (n) => {
    const x = Math.sin(i * 127.1 + n * 311.7) * 43758.5453;
    return x - Math.floor(x);
  };
  return {
    x: `${(r(1) * 100).toFixed(2)}%`,
    y: `${(r(2) * 100).toFixed(2)}%`,
    s: 1.5 + r(3) * 2,
    delay: `${(r(4) * 6).toFixed(2)}s`,
    dur: `${(3 + r(5) * 4).toFixed(2)}s`,
  };
});

const Screw = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" fill="rgba(244,244,239,0.05)" stroke="rgba(244,244,239,0.22)" strokeWidth="1.5" />
    <line x1="6.5" y1="12" x2="17.5" y2="12" stroke="rgba(244,244,239,0.28)" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Live video layer — src attaches after page load so first paint stays fast.
export const BgVideo = ({ src, className = "" }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const [ready, setReady] = useState(false);
  const [live, setLive] = useState(false);
  const low = isLowPower();

  useEffect(() => {
    if (reduce || low || isSlowConnection()) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const start = () => {
      el.muted = true;
      el.setAttribute("muted", "");
      el.src = src;
      el.play().then(() => setLive(true)).catch(() => {});
    };
    // wait for page load before the multi-MB backdrop starts downloading
    let id;
    let loadHandler;
    const schedule = () => {
      id = window.requestIdleCallback ? window.requestIdleCallback(start, { timeout: 3000 }) : setTimeout(start, 1800);
    };
    if (document.readyState === "complete") schedule();
    else {
      loadHandler = () => schedule();
      window.addEventListener("load", loadHandler, { once: true });
    }
    // pause the backdrop video when the tab is hidden — free GPU for everyone
    const onVis = () => {
      if (!el.src) return;
      if (document.hidden) el.pause();
      else el.play().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      if (loadHandler) window.removeEventListener("load", loadHandler);
      if (window.cancelIdleCallback) window.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, [reduce, src, low]);

  if (reduce || low) return null;
  return (
    <video
      ref={ref}
      muted
      loop
      playsInline
      preload="none"
      aria-hidden="true"
      disablePictureInPicture
      disableRemotePlayback
      controlsList="nodownload noplaybackrate noremoteplayback"
      onCanPlay={() => setReady(true)}
      className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1600ms] ${ready && live ? "opacity-35" : "opacity-0"} ${className}`}
    />
  );
};

export const LiquidGlassBackground = () => {
  const reduce = useReducedMotion();
  const low = isLowPower();
  const { pathname } = useLocation();
  const bg = BG_BY_ROUTE[routeKey(pathname)] || DEFAULT_BG;
  const stars = low ? STARS.slice(0, 18) : STARS;

  return (
    <div
      data-testid="liquid-glass-background"
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
    >
      {/* living space base — static layer (wobble/parallax removed for speed);
          life comes from the video, twinkling stars and drifting glows */}
      <div className="absolute inset-0">
        {/* route-specific 4K environment; crossfades on page change */}
        <AnimatePresence mode="sync">
          <motion.div
            key={bg.poster}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: "easeInOut" }}
          >
            <img
              src={bg.poster}
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-30"
            />
            {bg.video && <BgVideo src={bg.video} />}
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-paper/70 via-paper/50 to-paper/75" />

      {/* faint gel sheen drifting slowly across the space base */}
      {!reduce && !low && <div className="gel-sheen" aria-hidden="true" />}

      {/* twinkling starfield */}
      <div className="absolute inset-0">
        {stars.map((s, i) => (
          <span
            key={i}
            className="star-dot"
            style={{
              left: s.x,
              top: s.y,
              width: s.s,
              height: s.s,
              animationDelay: s.delay,
              animationDuration: s.dur,
              animationPlayState: reduce ? "paused" : "running",
            }}
          />
        ))}
      </div>

      {BLOBS.map((b, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full will-change-transform"
          style={{
            left: b.x,
            top: b.y,
            width: b.size,
            height: b.size,
            background: `radial-gradient(circle at 35% 35%, ${b.color}, transparent 65%)`,
            opacity: 0.7,
          }}
          animate={
            reduce
              ? {}
              : {
                  x: [0, i % 2 === 0 ? 60 : -60, 0],
                  y: [0, i % 3 === 0 ? -50 : 50, 0],
                  scale: [1, 1.15, 1],
                }
          }
          transition={{ duration: b.dur, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}

      {SCREWS.map((s, i) => (
        <motion.div
          key={`s${i}`}
          className="absolute will-change-transform"
          style={{ left: s.x, top: s.y }}
          animate={
            reduce
              ? {}
              : { y: [0, -34, 0], x: [0, i % 2 ? 14 : -14, 0], rotate: [0, 360] }
          }
          transition={{ duration: s.d, delay: i * 1.3, repeat: Infinity, ease: i % 2 ? "linear" : "easeInOut" }}
        >
          <Screw size={s.s} />
        </motion.div>
      ))}

      {BOKEH.map((b, i) => (
        <motion.span
          key={`k${i}`}
          className="absolute rounded-full will-change-transform"
          style={{
            left: b.x,
            top: b.y,
            width: b.s,
            height: b.s,
            background: b.c,
            filter: "blur(2.5px)",
          }}
          animate={reduce ? {} : { y: [0, -46, 0], opacity: [0.25, 0.9, 0.25] }}
          transition={{ duration: b.d, delay: i * 0.7, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
};

export const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 28, mass: 0.4 });
  return (
    <motion.div
      data-testid="scroll-progress"
      aria-hidden="true"
      className="fixed inset-x-0 top-0 z-[55] h-[2.5px] origin-left bg-gradient-to-r from-volt via-accent to-magenta"
      style={{ scaleX }}
    />
  );
};
